import { useCallback } from 'react';
import { toInteger } from 'lodash';
import { toast } from 'react-toastify';
import { StringParam, useQueryParams } from 'use-query-params';
import type { MarkResponseFormValues } from 'feature-components/mark-response/mark-response-form/mark-response-form.schema';
import { friendlyMarkStatus } from 'helpers/domain-specific/mark-status.helpers';
import { clearMarkingCache } from 'helpers/domain-specific/marking-cache.helpers';
import { pluralise } from 'helpers/string.helpers';
import {
  useDeleteTestInstanceResponseMarkItems,
  usePatchTestInstanceResponseMarkItems,
  usePostTestInstanceResponseMarkItems
} from 'services/assist-api/entities/test-instance-response-mark-item/hooks';
import type {
  PatchTestInstanceResponseMarkItem,
  PostTestInstanceResponseMarkItem
} from 'services/assist-api/entities/test-instance-response-mark-item/types';
import {
  usePatchTestInstanceResponseMark,
  usePostTestInstanceResponseMark
} from 'services/assist-api/entities/test-instance-response-mark/hooks';
import type {
  PatchTestInstanceResponseMark,
  PostTestInstanceResponseMark,
  TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import {
  markFieldsToSave,
  markItemIdsToDelete,
  markItemsToPatch,
  markItemsToPost
} from './use-save-handler.helpers';

export default function useSaveHandler(
  onSave: () => void,
  responseId: number,
  rubricId: number,
  defaultValues: MarkResponseFormValues,
  mark?: TestInstanceResponseMarkWithItems
): {
  updateTestInstanceResponseMark: (formValues: MarkResponseFormValues) => void;
  createTestInstanceResponseMark: (formValues: MarkResponseFormValues) => void;
  isSaving: boolean;
} {
  const [queryParams] = useQueryParams({
    // The ID of the auto-scored mark that will be replaced if the mark is created. Unlike the
    // relationship in the API it is not called "replacedTestInstanceResponseMark" or similar in the
    // URL as it has not been replaced at this point. That name would be misleading to the end user.
    oldMarkId: StringParam
  });

  const deleteTestInstanceResponseMarkItems = useDeleteTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to unscore ${rejected} ${pluralise('mark item', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Unscored ${fulfilled} ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to unscore mark items: ${error.message}`);
    }
  });

  const postTestInstanceResponseMarkItems = usePostTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to score ${rejected} new ${pluralise('mark item', rejected)}.`);
      }
      if (fulfilled) {
        toast.success(`Scored ${fulfilled} new ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to score new mark items: ${error.message}`);
    }
  });

  const patchTestInstanceResponseMarkItems = usePatchTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(
          `Failed to update the scores of ${rejected} ${pluralise('mark item', rejected)}.`
        );
      }
      if (fulfilled) {
        toast.success(`Updated the scores of ${fulfilled} ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update the scores of mark items: ${error.message}`);
    }
  });

  const patchTestInstanceResponseMark = usePatchTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      toast.success(`Updated mark with status: ${friendlyMarkStatus(data.json.status)}.`);
    },
    onError: (error) => {
      toast.error(`Failed to update mark: ${error.message}`);
    }
  });

  const postTestInstanceResponseMark = usePostTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      toast.success(`Created mark with status: ${friendlyMarkStatus(data.json.status)}.`);
    },
    onError: (error) => {
      toast.error(`Failed to create mark: ${error.message}`);
    }
  });

  const clearMarkingCacheAndRedirect = useCallback(() => {
    clearMarkingCache();
    onSave();
  }, [onSave]);

  /**
   * Updates a mark. For PATCH requests, the API can't handle mark items being saved as part of the
   * same request, so we need to send multiple requests and batch them together for saving the items
   * alongside the body of the mark.
   */
  const updateTestInstanceResponseMark = useCallback(
    async (formValues: MarkResponseFormValues) => {
      if (mark) {
        const markToPatch: PatchTestInstanceResponseMark = {
          id: mark.id,
          // We shouldn't specify an old mark ID when updating, as auto-scored marks should only be
          // replaced when a new replacement mark is created:
          data: markFieldsToSave(responseId, rubricId, formValues, defaultValues)
        };
        const itemIdsToDelete: number[] = markItemIdsToDelete(formValues.markItems);
        const itemsToPatch: PatchTestInstanceResponseMarkItem[] = markItemsToPatch(
          formValues.markItems,
          toInteger(formValues.activeMarkerId),
          mark
        );
        const itemsToPost: PostTestInstanceResponseMarkItem[] = markItemsToPost(
          formValues.markItems,
          toInteger(formValues.activeMarkerId),
          mark.id
        );
        await patchTestInstanceResponseMark.mutateAsync(markToPatch);
        if (itemIdsToDelete.length > 0) {
          await deleteTestInstanceResponseMarkItems.mutateAsync(itemIdsToDelete);
        }
        if (itemsToPatch.length > 0) {
          await patchTestInstanceResponseMarkItems.mutateAsync(itemsToPatch);
        }
        if (itemsToPost.length > 0) {
          await postTestInstanceResponseMarkItems.mutateAsync(itemsToPost);
        }
        clearMarkingCacheAndRedirect();
      }
    },
    [
      mark,
      responseId,
      rubricId,
      defaultValues,
      patchTestInstanceResponseMark,
      clearMarkingCacheAndRedirect,
      deleteTestInstanceResponseMarkItems,
      patchTestInstanceResponseMarkItems,
      postTestInstanceResponseMarkItems
    ]
  );

  /**
   * Creates a mark, also creating any mark items that have been scored as part of the same network
   * request.
   */
  const createTestInstanceResponseMark = useCallback(
    async (formValues: MarkResponseFormValues) => {
      const testInstanceResponseMark: PostTestInstanceResponseMark = {
        ...markFieldsToSave(
          responseId,
          rubricId,
          formValues,
          defaultValues,
          queryParams.oldMarkId ? toInteger(queryParams.oldMarkId) : undefined
        ),
        testInstanceResponseMarkItems: markItemsToPost(
          formValues.markItems,
          toInteger(formValues.activeMarkerId)
        )
      };
      await postTestInstanceResponseMark.mutateAsync(testInstanceResponseMark);
      clearMarkingCacheAndRedirect();
    },
    [
      responseId,
      rubricId,
      defaultValues,
      queryParams.oldMarkId,
      postTestInstanceResponseMark,
      clearMarkingCacheAndRedirect
    ]
  );

  const isSaving =
    postTestInstanceResponseMark.isLoading ||
    patchTestInstanceResponseMark.isLoading ||
    postTestInstanceResponseMarkItems.isLoading ||
    patchTestInstanceResponseMarkItems.isLoading ||
    deleteTestInstanceResponseMarkItems.isLoading;

  return {
    createTestInstanceResponseMark,
    updateTestInstanceResponseMark,
    isSaving
  };
}
